import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useLeague } from './LeagueContext';

const AppContext = createContext({});

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within AppProvider');
  return context;
};

export const AppProvider = ({ children }) => {
  const { activeLeague } = useLeague();
  const [players, setPlayers] = useState([]);
  const [games, setGames] = useState([]);
  const [gameStats, setGameStats] = useState([]);
  const [playersLoading, setPlayersLoading] = useState(false);
  const [gamesLoading, setGamesLoading] = useState(false);
  const [playersError, setPlayersError] = useState(null);
  const [gamesError, setGamesError] = useState(null);

  const fetchPlayers = useCallback(async () => {
    if (!supabase || !activeLeague) {
      setPlayers([]);
      return;
    }
    setPlayersLoading(true);
    setPlayersError(null);
    try {
      const { data, error } = await supabase
        ?.from('players')
        ?.select('*')
        ?.eq('league_id', activeLeague.id)
        ?.order('created_at', { ascending: true });
      if (error) throw error;
      setPlayers(data || []);
    } catch (err) {
      setPlayersError(err?.message || 'Failed to load players');
    } finally {
      setPlayersLoading(false);
    }
  }, [activeLeague]);

  const fetchGamesWithStats = useCallback(async () => {
    if (!supabase || !activeLeague) {
      setGames([]);
      setGameStats([]);
      return;
    }
    setGamesLoading(true);
    setGamesError(null);
    try {
      const gamesResult = await supabase
        .from('games')
        .select('*')
        .eq('league_id', activeLeague.id)
        .order('date', { ascending: false });
      if (gamesResult?.error) throw gamesResult.error;

      const gamesList = gamesResult?.data || [];
      setGames(gamesList);

      if (gamesList.length === 0) {
        setGameStats([]);
      } else {
        const gameIds = gamesList.map((g) => g.id);
        const statsResult = await supabase
          .from('game_stats')
          .select('*, players(id, name, position, avatar_url)')
          .in('game_id', gameIds);
        if (statsResult?.error) throw statsResult.error;
        setGameStats(statsResult?.data || []);
      }
    } catch (err) {
      setGamesError(err?.message || 'Failed to load games');
    } finally {
      setGamesLoading(false);
    }
  }, [activeLeague]);

  useEffect(() => {
    fetchPlayers();
    fetchGamesWithStats();
  }, [fetchPlayers, fetchGamesWithStats]);

  // Computed: player stats aggregated from game_stats
  const getPlayerStats = useCallback((playerId) => {
    const stats = gameStats?.filter(s => s?.player_id === playerId);
    if (!stats?.length) return null;
    const total = stats?.length;
    const wins = games?.filter(g => {
      const playerStat = stats?.find(s => s?.game_id === g?.id);
      if (!playerStat) return false;
      return g?.winning_team === playerStat?.team;
    })?.length;
    return {
      gamesPlayed: total,
      wins,
      losses: total - wins,
      winPct: total > 0 ? Math.round((wins / total) * 100) : 0,
      avgPoints: total > 0 ? parseFloat((stats?.reduce((s, r) => s + (r?.points || 0), 0) / total)?.toFixed(1)) : 0,
      avgRebounds: total > 0 ? parseFloat((stats?.reduce((s, r) => s + (r?.rebounds || 0), 0) / total)?.toFixed(1)) : 0,
      avgAssists: total > 0 ? parseFloat((stats?.reduce((s, r) => s + (r?.assists || 0), 0) / total)?.toFixed(1)) : 0,
      avgSteals: total > 0 ? parseFloat((stats?.reduce((s, r) => s + (r?.steals || 0), 0) / total)?.toFixed(1)) : 0,
      avgBlocks: total > 0 ? parseFloat((stats?.reduce((s, r) => s + (r?.blocks || 0), 0) / total)?.toFixed(1)) : 0,
    };
  }, [gameStats, games]);

  // Computed: duo & trio win rates from game_stats
  const getComboStats = useCallback(() => {
    const teamGroups = {};
    gameStats?.forEach((stat) => {
      const key = `${stat?.game_id}__${stat?.team}`;
      if (!teamGroups[key]) teamGroups[key] = { gameId: stat?.game_id, team: stat?.team, playerIds: [] };
      teamGroups[key].playerIds.push(stat?.player_id);
    });

    const comboCounts = {};
    const gameMap = {};
    games?.forEach((g) => { gameMap[g?.id] = g; });

    Object.values(teamGroups).forEach((group) => {
      const { gameId, team, playerIds } = group;
      const game = gameMap[gameId];
      if (!game) return;
      const isWin = game.winning_team === team;

      for (let i = 0; i < playerIds.length; i++) {
        for (let j = i + 1; j < playerIds.length; j++) {
          const key = [playerIds[i], playerIds[j]].sort().join('-');
          if (!comboCounts[key]) comboCounts[key] = { playerIds: [playerIds[i], playerIds[j]].sort(), games: 0, wins: 0, size: 2 };
          comboCounts[key].games++;
          if (isWin) comboCounts[key].wins++;
        }
      }

      for (let i = 0; i < playerIds.length; i++) {
        for (let j = i + 1; j < playerIds.length; j++) {
          for (let k = j + 1; k < playerIds.length; k++) {
            const key = [playerIds[i], playerIds[j], playerIds[k]].sort().join('-');
            if (!comboCounts[key]) comboCounts[key] = { playerIds: [playerIds[i], playerIds[j], playerIds[k]].sort(), games: 0, wins: 0, size: 3 };
            comboCounts[key].games++;
            if (isWin) comboCounts[key].wins++;
          }
        }
      }
    });

    const playerMap = {};
    players?.forEach((p) => { playerMap[p?.id] = p?.name; });

    const allCombos = Object.values(comboCounts)
      .filter((c) => c.games >= 2)
      .map((c) => ({
        ...c,
        playerNames: c.playerIds.map((id) => playerMap[id] || 'Unknown'),
        winPct: Math.round((c.wins / c.games) * 100),
      }))
      .sort((a, b) => b.winPct - a.winPct || b.games - a.games);

    return {
      duos: allCombos.filter((c) => c.size === 2),
      trios: allCombos.filter((c) => c.size === 3),
    };
  }, [gameStats, games, players]);

  const value = {
    players,
    games,
    gameStats,
    playersLoading,
    gamesLoading,
    playersError,
    gamesError,
    fetchPlayers,
    fetchGamesWithStats,
    refetchAll: useCallback(async () => {
      await Promise.all([fetchPlayers(), fetchGamesWithStats()]);
    }, [fetchPlayers, fetchGamesWithStats]),
    getPlayerStats,
    getComboStats,
    deleteGame: useCallback(async (gameId) => {
      if (!supabase) throw new Error('Database not connected');
      const { error } = await supabase.from('games').delete().eq('id', gameId);
      if (error) throw error;
      await fetchGamesWithStats();
    }, [fetchGamesWithStats]),
    updateGame: useCallback(async (gameId, gameFields, statsArray) => {
      if (!supabase) throw new Error('Database not connected');
      const { error: gameError } = await supabase.from('games').update(gameFields).eq('id', gameId);
      if (gameError) throw gameError;
      const { error: deleteError } = await supabase.from('game_stats').delete().eq('game_id', gameId);
      if (deleteError) throw deleteError;
      const { error: statsError } = await supabase.from('game_stats').insert(statsArray);
      if (statsError) throw statsError;
      await fetchGamesWithStats();
    }, [fetchGamesWithStats]),
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export default AppContext;
